import * as DialogPrimitive from '@radix-ui/react-dialog';
import * as MenuPrimitive from '@radix-ui/react-dropdown-menu';
import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent as ReactKeyboardEvent, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react';
import { isApiError } from '../api/http';
import { apiErrorText, useI18n } from '../i18n';
import { Form, TextField } from './fields';
import { Icon, type IconName, type IconSize } from './icon';

// The icon component is part of the kit (TF-97), and so is a screen's main action (TF-90).
export { Icon, type IconName, type IconSize } from './icon';
export { MainAction, useScrolled } from './fab';
// Material's lists (TF-95).
export { dotted, List, ListItem, ListSkeleton } from './list';
// Tooltips (TF-96).
export { HoverCard, RichTooltip, TooltipLayer } from './tooltip';
// The form controls (TF-91).
export { Checkbox, FieldError, fieldErrorOf, FieldGroupError, Form, LONG_LIST, Radio, SelectField, Switch, TextArea, TextField, type SelectGroup, type SelectOption } from './fields';
export { DateField, DateRangeField, DateTimeField, DayMonthField, TimeField, type DateRange } from './dates';
export { PageTitle, PaneProvider, TwoPane, useExpanded, useNarrow, usePane, useRecordLink, type Pane } from './panes';
// A section's row of pages, the rest under "…" when they do not fit (TF-215).
export { fittingLinks, SectionNav, type SectionLink } from './section-nav';
export { InlineField, type InlineEditorProps } from './inline-field';
export { Tabs, type TabItem } from './tabs';

// The few building blocks every page uses. Dialogs and menus are Radix primitives (focus trap,
// Escape, aria roles and keyboard handling included); the look comes from global.css.

// ---------------------------------------------------------------------------------- Dialog

/**
 * The confirming button of the form in a dialog, mirrored (TF-94): on a phone a dialog with a form
 * takes the whole screen and its confirming action sits in the top bar, as Material's full-screen
 * dialog has it. The form keeps its own button; this one asks the form to submit through it, and
 * follows its label and whether it is enabled.
 */
function useFormSubmit(content: HTMLElement | null, open: boolean): { label: string; disabled: boolean; submit: () => void } | null {
  const [state, setState] = useState<{ label: string; disabled: boolean } | null>(null);
  useEffect(() => {
    if (!open || !content) return undefined;
    const read = () => {
      const button = content.querySelector<HTMLButtonElement>('form button[type="submit"]:not(.dialog-submit-proxy)');
      setState(button ? { label: button.textContent?.trim() ?? '', disabled: button.disabled } : null);
    };
    read();
    const watcher = new MutationObserver(read);
    watcher.observe(content, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ['disabled'] });
    return () => watcher.disconnect();
  }, [content, open]);
  if (!state || !content) return null;
  return {
    ...state,
    submit: () => {
      const button = content.querySelector<HTMLButtonElement>('form button[type="submit"]:not(.dialog-submit-proxy)');
      button?.form?.requestSubmit(button);
    },
  };
}

/**
 * Where focus goes when a dialog closes. Radix returns it to a Dialog.Trigger, and these dialogs are
 * opened from state with no trigger, so focus fell to the page. This remembers what had focus when
 * the dialog opened (a button, or the picker that asked for a nested New account dialog, TF-236) and
 * goes back there while it is still on the page. A layout effect: it runs before Radix moves focus in.
 */
function useReturnFocus(open: boolean, content: HTMLElement | null): (event: Event) => void {
  const opener = useRef<HTMLElement | null>(null);
  useLayoutEffect(() => {
    if (open) opener.current = document.activeElement instanceof HTMLElement && document.activeElement !== document.body ? document.activeElement : null;
  }, [open]);
  return (event) => {
    const target = opener.current;
    opener.current = null;
    const active = document.activeElement;
    // Focus moved elsewhere on purpose (a click outside): leave it there.
    const stillOurs = !active || active === document.body || !!content?.contains(active);
    if (!target?.isConnected || !stillOurs) return;
    event.preventDefault();
    target.focus({ preventScroll: true });
  };
}

const TABBABLE = 'a[href], button, input, select, textarea, [tabindex], [contenteditable="true"]';

/** The elements Tab stops at inside `root`, in document order. */
function tabbables(root: HTMLElement): HTMLElement[] {
  return [...root.querySelectorAll<HTMLElement>(TABBABLE)].filter(
    (element) => element.tabIndex >= 0 && !(element as HTMLButtonElement).disabled && !(element instanceof HTMLInputElement && element.type === 'hidden') && element.getClientRects().length > 0,
  );
}

/**
 * Tab inside a dialog always reaches the next field (TF-294). Radix's focus trap moves the focus to the
 * dialog itself when the page changes while focus is between two elements - a field's blur that
 * removes something (a hint replaced by an error message) - and Tab then bounced between two places.
 * This remembers where Tab started and, when the focus lands on the dialog itself straight after,
 * goes on to the element Tab was heading for.
 */
function useSteadyTab(content: HTMLElement | null): void {
  useEffect(() => {
    if (!content) return;
    let pending: { from: HTMLElement; back: boolean; at: number } | null = null;
    const keydown = (event: KeyboardEvent) => {
      pending = event.key === 'Tab' && event.target instanceof HTMLElement && event.target !== content ? { from: event.target, back: event.shiftKey, at: performance.now() } : null;
    };
    const focusin = (event: FocusEvent) => {
      const tab = pending;
      pending = null;
      if (!tab || event.target !== content || performance.now() - tab.at > 500) return;
      const stops = tabbables(content);
      const after = (element: HTMLElement) => Boolean(tab.from.compareDocumentPosition(element) & Node.DOCUMENT_POSITION_FOLLOWING);
      const next = tab.back ? [...stops].reverse().find((element) => !after(element) && element !== tab.from) : stops.find(after);
      (next ?? (tab.back ? stops[stops.length - 1] : stops[0]))?.focus();
    };
    content.addEventListener('keydown', keydown, true);
    content.addEventListener('focusin', focusin);
    return () => {
      content.removeEventListener('keydown', keydown, true);
      content.removeEventListener('focusin', focusin);
    };
  }, [content]);
}

/**
 * A dialog opens with the focus on its field marked `autoFocus` (TF-294). React focuses such a field
 * as it renders, before the dialog's focus trap is on: opened over another dialog ("+ New contact"
 * from a picker), the outer trap took the focus back and the new dialog then fell back to its first
 * button. Radix calls this once its trap is on (an event on the dialog's content), so the field gets
 * the focus for good.
 */
/**
 * A click on the snackbar (its action, say "Share with others…", TF-284) is not a click outside the
 * dialog: the dialog stays, and what the action opens comes over it.
 */
function keepForSnackbar(event: { target: EventTarget | null; preventDefault: () => void }): void {
  if (event.target instanceof Element && event.target.closest('.snackbar-host')) event.preventDefault();
}

function focusOnOpen(event: Event): void {
  const field = event.target instanceof HTMLElement ? event.target.querySelector<HTMLElement>('[data-autofocus]') : null;
  if (!field) return;
  event.preventDefault();
  field.focus({ preventScroll: true });
}

export function Dialog({
  open,
  onOpenChange,
  title,
  description,
  wide,
  icon,
  children,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  wide?: boolean;
  /** Above a centred title, for a dialog that asks one thing (Material's basic dialog with an icon). */
  icon?: IconName;
  children: ReactNode;
}) {
  const { t } = useI18n();
  const [content, setContent] = useState<HTMLDivElement | null>(null);
  const formSubmit = useFormSubmit(content, open);
  const returnFocus = useReturnFocus(open, content);
  useSteadyTab(content);
  const className = ['dialog', wide && 'dialog-wide', icon && 'dialog-with-icon'].filter(Boolean).join(' ');
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="dialog-overlay" />
        <DialogPrimitive.Content
          ref={setContent}
          className={className}
          // With a description Radix links it (aria-describedby); without one, an explicit undefined
          // says there is none on purpose. Passing undefined always cut the link to the description.
          {...(description ? {} : { 'aria-describedby': undefined })}
          onOpenAutoFocus={focusOnOpen}
          onCloseAutoFocus={returnFocus}
          onInteractOutside={keepForSnackbar}
        >
          {icon && (
            <div className="dialog-icon" aria-hidden>
              <Icon name={icon} />
            </div>
          )}
          <div className="dialog-head">
            <DialogPrimitive.Title asChild>
              <h2>{title}</h2>
            </DialogPrimitive.Title>
            {formSubmit && (
              <button type="button" className="btn btn-text dialog-submit-proxy" disabled={formSubmit.disabled} onClick={formSubmit.submit}>
                {formSubmit.label}
              </button>
            )}
            <DialogPrimitive.Close className="icon-btn dialog-close" aria-label={t('common.close')}>
              <Icon name="close" />
            </DialogPrimitive.Close>
          </div>
          {description ? (
            <DialogPrimitive.Description className="hint">{description}</DialogPrimitive.Description>
          ) : (
            <DialogPrimitive.Description className="visually-hidden">{title}</DialogPrimitive.Description>
          )}
          {children}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

/**
 * A panel from the right that leaves the page behind it in view (a quick look at one thing, TF-17);
 * on a phone it takes the whole screen. The same Radix dialog underneath: focus moves in and comes
 * back to what opened it, and Escape closes it.
 */
export function Sheet({
  open,
  onOpenChange,
  title,
  titleIcon,
  children,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  titleIcon?: ReactNode;
  children: ReactNode;
}) {
  const { t } = useI18n();
  const [content, setContent] = useState<HTMLDivElement | null>(null);
  useSteadyTab(content);
  // Back to what opened it, a button in a dialog under it too (Link to… from the preview, TF-284).
  const returnFocus = useReturnFocus(open, content);
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="dialog-overlay sheet-overlay" />
        <DialogPrimitive.Content ref={setContent} className="sheet" onOpenAutoFocus={focusOnOpen} onCloseAutoFocus={returnFocus} onInteractOutside={keepForSnackbar}>
          <div className="dialog-head sheet-head">
            <DialogPrimitive.Title asChild>
              <h2>
                {titleIcon}
                {title}
              </h2>
            </DialogPrimitive.Title>
            <DialogPrimitive.Close className="icon-btn sheet-close" aria-label={t('common.close')}>
              <Icon name="close" />
            </DialogPrimitive.Close>
          </div>
          <DialogPrimitive.Description className="visually-hidden">{title}</DialogPrimitive.Description>
          {children}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

/** A dialog with one text field (new folder, rename): Enter submits, errors show inline. */
export function PromptDialog({
  open,
  onOpenChange,
  title,
  label,
  initial = '',
  submitLabel,
  onSubmit,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  label: string;
  initial?: string;
  submitLabel: string;
  onSubmit: (value: string) => Promise<void>;
}) {
  const { t } = useI18n();
  const [value, setValue] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const reset = (next: boolean) => {
    if (next) {
      setValue(initial);
      setError(null);
    }
    onOpenChange(next);
  };
  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await onSubmit(value.trim());
      onOpenChange(false);
    } catch (caught) {
      setError(errorMessage(caught, t('common.somethingWrong')));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={reset} title={title}>
      <Form onSubmit={() => void submit()}>
        <TextField id="prompt-value" label={label} type="text" value={value} onChange={setValue} error={error} required maxLength={255} autoFocus autoComplete="off" />
        <div className="dialog-foot">
          <button type="button" className="btn" onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </button>
          <button type="submit" className="btn btn-primary" disabled={busy || !value.trim()}>
            {submitLabel}
          </button>
        </div>
      </Form>
    </Dialog>
  );
}

/** "Are you sure?" for irreversible actions. */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  message,
  confirmLabel,
  danger,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  message: string;
  confirmLabel: string;
  danger?: boolean;
  onConfirm: () => Promise<void>;
}) {
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  return (
    <Dialog open={open} onOpenChange={onOpenChange} title={title}>
      <p>{message}</p>
      <div className="dialog-foot">
        <button type="button" className="btn" onClick={() => onOpenChange(false)}>
          {t('common.cancel')}
        </button>
        <button
          type="button"
          className={danger ? 'btn btn-danger' : 'btn btn-primary'}
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            try {
              await onConfirm();
              onOpenChange(false);
            } catch (error) {
              toast.error(errorMessage(error, t('common.somethingWrong')));
            } finally {
              setBusy(false);
            }
          }}
        >
          {confirmLabel}
        </button>
      </div>
    </Dialog>
  );
}

// ------------------------------------------------------------------------------------ Menu

export interface MenuAction {
  label: string;
  icon?: ReactNode;
  onSelect: () => void;
  danger?: boolean;
  hidden?: boolean;
  /** A shortcut or a count, at the end of the item. */
  trailing?: string;
}

/** Whether the window is a phone's (Material's compact width, below 600). */
export function useCompact(): boolean {
  const query = '(max-width: 599px)';
  const [compact, setCompact] = useState(() => typeof window !== 'undefined' && window.matchMedia(query).matches);
  useEffect(() => {
    const media = window.matchMedia(query);
    const change = () => setCompact(media.matches);
    media.addEventListener('change', change);
    return () => media.removeEventListener('change', change);
  }, []);
  return compact;
}

/** More than this many items in a menu on a phone, and it opens as a bottom sheet (TF-94). */
const MENU_ITEMS_IN_A_POPUP = 5;

/**
 * Material's bottom sheet (TF-94): up from the bottom edge, with a drag handle. Drag it down (or tap
 * the scrim, or press Escape) to close it; focus comes back to what opened it.
 */
export function BottomSheet({ open, onOpenChange, title, children }: { open: boolean; onOpenChange: (open: boolean) => void; title: string; children: ReactNode }) {
  const [dragged, setDragged] = useState(0);
  const start = useRef<number | null>(null);
  const down = (event: ReactPointerEvent<HTMLDivElement>) => {
    start.current = event.clientY;
    event.currentTarget.setPointerCapture(event.pointerId);
  };
  const move = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (start.current !== null) setDragged(Math.max(0, event.clientY - start.current));
  };
  const up = () => {
    if (dragged > 80) onOpenChange(false);
    start.current = null;
    setDragged(0);
  };
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="dialog-overlay" />
        <DialogPrimitive.Content className="sheet sheet-bottom" style={dragged ? { transform: `translateY(${dragged}px)` } : undefined}>
          <div className="sheet-handle" aria-hidden onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up} />
          <DialogPrimitive.Title asChild>
            <h2>{title}</h2>
          </DialogPrimitive.Title>
          <DialogPrimitive.Description className="visually-hidden">{title}</DialogPrimitive.Description>
          {children}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

/**
 * A "…" button with actions, for table rows; on a phone, a long list of them opens as a bottom sheet.
 * `icon` puts another icon on the button ("add" for a record's teams, TF-384).
 */
export function ActionsMenu({ label, actions, icon = 'more_horiz' }: { label: string; actions: (MenuAction | 'separator')[]; icon?: IconName }) {
  const compact = useCompact();
  const [sheetOpen, setSheetOpen] = useState(false);
  const visible = actions.filter((action) => action === 'separator' || !action.hidden);
  const items = visible.filter((action): action is MenuAction => action !== 'separator');
  if (items.length === 0) return null;
  if (compact && items.length > MENU_ITEMS_IN_A_POPUP) {
    return (
      <>
        <button type="button" className="icon-btn" aria-label={label} aria-haspopup="dialog" onClick={() => setSheetOpen(true)}>
          <Icon name={icon} />
        </button>
        <BottomSheet open={sheetOpen} onOpenChange={setSheetOpen} title={label}>
          <div role="menu" aria-label={label}>
            {visible.map((action, index) =>
              action === 'separator' ? (
                <div key={`sep-${index}`} className="menu-sep" role="separator" />
              ) : (
                <button
                  key={action.label}
                  type="button"
                  role="menuitem"
                  className={action.danger ? 'menu-item danger' : 'menu-item'}
                  onClick={() => {
                    setSheetOpen(false);
                    action.onSelect();
                  }}
                >
                  {action.icon}
                  {action.label}
                  {action.trailing && <span className="menu-item-trailing">{action.trailing}</span>}
                </button>
              ),
            )}
          </div>
        </BottomSheet>
      </>
    );
  }
  return (
    <MenuPrimitive.Root>
      <MenuPrimitive.Trigger className="icon-btn" aria-label={label}>
        <Icon name={icon} />
      </MenuPrimitive.Trigger>
      <MenuPrimitive.Portal>
        <MenuPrimitive.Content className="menu" align="end" sideOffset={4}>
          {visible.map((action, index) =>
            action === 'separator' ? (
              <MenuPrimitive.Separator key={`sep-${index}`} className="menu-sep" />
            ) : (
              <MenuPrimitive.Item key={action.label} className={action.danger ? 'menu-item danger' : 'menu-item'} onSelect={action.onSelect}>
                {action.icon}
                {action.label}
                {action.trailing && <span className="menu-item-trailing">{action.trailing}</span>}
              </MenuPrimitive.Item>
            ),
          )}
        </MenuPrimitive.Content>
      </MenuPrimitive.Portal>
    </MenuPrimitive.Root>
  );
}

export { MenuPrimitive };

// ------------------------------------------------------------------------------- Snackbar

/** An action a message offers, such as Undo (TF-94). Choosing it closes the message. */
export interface ToastAction {
  label: string;
  onAction: () => void;
}

export interface ToastOptions {
  action?: ToastAction;
  /** A second, for where the thing went: a deal moved on a phone's board offers Undo and Show (TF-181). */
  also?: ToastAction;
}

interface Toast {
  id: number;
  kind: 'info' | 'error';
  message: string;
  action?: ToastAction;
  also?: ToastAction;
  /** Closing: shown for its exit animation, then gone (TF-89). */
  leaving?: boolean;
}

interface ToastApi {
  info: (message: string, options?: ToastOptions) => void;
  error: (message: string, options?: ToastOptions) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

/** How long a message stays: 4 seconds, 10 with something to do; an error until it is closed. */
const SHOWN_MS = { plain: 4000, withAction: 10_000 } as const;
/** Its exit (TF-89): --md-sys-motion-duration-short3, the .snackbar-leaving animation. */
const LEAVING_MS = 150;

/**
 * Material's snackbar (TF-94): one message at a time - a new one replaces the one showing - at the
 * bottom, above the navigation bar and a screen's main button, in the inverse colours. It may offer
 * one action, such as Undo. An error stays until it is closed and is announced at once; the rest are
 * read out politely.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [current, setCurrent] = useState<Toast | null>(null);
  const next = useRef(1);
  const timer = useRef<number | undefined>(undefined);
  const { t } = useI18n();
  // It leaves with its exit animation; a new message pushed meanwhile replaces it at once.
  const dismiss = useCallback((id?: number) => {
    const leaving = (showing: Toast | null) => showing !== null && (id === undefined || showing.id === id);
    setCurrent((showing) => (leaving(showing) && !showing!.leaving ? { ...showing!, leaving: true } : showing));
    window.setTimeout(() => setCurrent((showing) => (leaving(showing) && showing!.leaving ? null : showing)), LEAVING_MS);
  }, []);
  const push = useCallback(
    (kind: Toast['kind'], message: string, options?: ToastOptions) => {
      const id = next.current++;
      window.clearTimeout(timer.current);
      setCurrent({ id, kind, message, action: options?.action, also: options?.also });
      if (kind === 'error') return;
      timer.current = window.setTimeout(() => dismiss(id), options?.action ? SHOWN_MS.withAction : SHOWN_MS.plain);
    },
    [dismiss],
  );
  const api = useMemo<ToastApi>(() => ({ info: (message, options) => push('info', message, options), error: (message, options) => push('error', message, options) }), [push]);
  const snackbar = current && (
    <div
      key={current.id}
      className={['toast snackbar', current.kind === 'error' && 'snackbar-error', current.leaving && 'snackbar-leaving'].filter(Boolean).join(' ')}
      role={current.kind === 'error' ? 'alert' : undefined}
    >
      {current.kind === 'error' && <Icon name="error" />}
      <span className="snackbar-text" title={current.message}>
        {current.message}
      </span>
      {[current.action, current.also].map(
        (action) =>
          action && (
            <button
              key={action.label}
              type="button"
              className="btn btn-text toast-action"
              onClick={() => {
                dismiss(current.id);
                action.onAction();
              }}
            >
              {action.label}
            </button>
          ),
      )}
      <button type="button" className="icon-btn snackbar-close" aria-label={t('common.close')} onClick={() => dismiss(current.id)}>
        <Icon name="close" />
      </button>
    </div>
  );
  return (
    <ToastContext.Provider value={api}>
      {children}
      {/* A live region that is always there, so a message that appears in it is read out politely; an
          error is an alert of its own, read out at once as it appears. */}
      <div className="toasts snackbar-host">
        <div role="status" aria-live="polite">
          {current?.kind === 'info' && snackbar}
        </div>
        {current?.kind === 'error' && snackbar}
      </div>
    </ToastContext.Provider>
  );
}

/**
 * Says what was just done and offers to take it back (TF-94): Undo runs `undo`, then says it was
 * undone (or why it could not be) and runs `after`, typically a refresh.
 */
export function useUndoToast(): (message: string, undo: () => Promise<unknown>, options?: { after?: () => unknown; undoneMessage?: string; also?: ToastAction }) => void {
  const toast = useToast();
  const { t } = useI18n();
  return useCallback(
    (message, undo, options) =>
      toast.info(message, {
        action: {
          label: t('common.undo'),
          onAction: () =>
            void undo()
              .then(async () => {
                toast.info(options?.undoneMessage ?? t('common.undone'));
                await options?.after?.();
              })
              .catch((failure: unknown) => toast.error(errorMessage(failure, t('common.somethingWrong')))),
        },
        also: options?.also,
      }),
    [toast, t],
  );
}

export function useToast(): ToastApi {
  const api = useContext(ToastContext);
  if (!api) throw new Error('useToast outside ToastProvider');
  return api;
}

/** The message to show for a failed call: the platform's own message, or a fallback. */
export function errorMessage(error: unknown, fallback: string): string {
  if (isApiError(error)) return apiErrorText(error.code, error.details) ?? (error.message || fallback);
  return fallback;
}

// ------------------------------------------------------------------------------- Small bits

/** Material's circular progress indicator (TF-96), with what is loading beside it. */
export function Spinner({ label }: { label?: string }) {
  return (
    <div className="loading-line" role="status">
      <CircularProgress />
      <span>{label}</span>
    </div>
  );
}

/** Material's linear progress indicator: how far something that is known to end has got (0 to 100). */
export function ProgressBar({ value, label }: { value: number; label: string }) {
  const share = Math.max(0, Math.min(100, Math.round(value)));
  return (
    <div className="progress" role="progressbar" aria-label={label} aria-valuenow={share} aria-valuemin={0} aria-valuemax={100}>
      <span style={{ width: `${share}%` }} />
    </div>
  );
}

/** The indicator alone: a track and an arc that grows and turns while it is not known how long. */
export function CircularProgress() {
  return (
    <span className="spinner" aria-hidden>
      <svg viewBox="0 0 48 48">
        <circle className="spinner-track" cx="24" cy="24" r="20" fill="none" strokeWidth="4" />
        <circle className="spinner-arc" cx="24" cy="24" r="20" fill="none" strokeWidth="4" />
      </svg>
    </span>
  );
}

/**
 * Arrow keys (and Home, End) move between the tabs of a tablist and choose the one reached, as
 * Material's and ARIA's tabs do (TF-96). Put on the element with role="tablist".
 */
export function tablistKeys(event: ReactKeyboardEvent<HTMLElement>): void {
  const tabs = [...event.currentTarget.querySelectorAll<HTMLElement>('[role="tab"]:not([disabled])')];
  const at = tabs.indexOf(document.activeElement as HTMLElement);
  if (at < 0) return;
  const next =
    event.key === 'ArrowRight' ? (at + 1) % tabs.length : event.key === 'ArrowLeft' ? (at - 1 + tabs.length) % tabs.length : event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length - 1 : -1;
  if (next < 0) return;
  event.preventDefault();
  tabs[next]!.focus();
  tabs[next]!.click();
}

export function EmptyState({ icon, title, children }: { icon: ReactNode; title: string; children?: ReactNode }) {
  return (
    <div className="empty">
      {icon}
      <h2>{title}</h2>
      {children}
    </div>
  );
}

const byExtension: [RegExp, IconName][] = [
  [/\.(png|jpe?g|gif|webp|bmp|tiff?|heic|svg)$/i, 'image'],
  [/\.(xlsx?|ods|csv)$/i, 'table_chart'],
  [/\.(zip|7z|rar|gz|tar)$/i, 'folder_zip'],
  [/\.(mp3|wav|m4a|ogg|flac)$/i, 'audio_file'],
  [/\.(mp4|mov|avi|mkv|webm)$/i, 'video_file'],
  [/\.(pdf|docx?|odt|rtf|txt|md|pptx?|odp)$/i, 'description'],
];

export function FileTypeIcon({ name, size = 20 }: { name: string; size?: IconSize }) {
  return <Icon name={byExtension.find(([pattern]) => pattern.test(name))?.[1] ?? 'draft'} size={size} />;
}

export function FolderTypeIcon({ restricted, size = 20 }: { restricted?: boolean; size?: IconSize }) {
  return <Icon name={restricted ? 'folder_managed' : 'folder'} size={size} />;
}

/** Title highlighted by the search engine: matches arrive wrapped in …, never as HTML. */
export function Highlighted({ text }: { text: string }) {
  const parts = text.split(/([^]*)/);
  return (
    <>
      {parts.map((part, index) =>
        part.startsWith('') ? <mark key={index}>{part.slice(1, -1)}</mark> : <span key={index}>{part}</span>,
      )}
    </>
  );
}
